// Shared helpers for News rendering — used by NewsSection card list,
// the modal-less detail page, and SEO meta generation.

export interface NewsItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content?: string | null;
  date: string;
  tag: string;
  images: unknown;
  link?: string | null;
  cover_image?: string | null;
  cover_alt?: string | null;
}

export const SITE_URL = 'https://kuznialisewo.pl';
export const SITE_NAME = 'Kuźnia Inicjatyw Lokalnych w Lisewie';
export const DEFAULT_OG_IMAGE =
  'https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/33a20492-103c-4bec-b67f-715ad59e1e39/id-preview-0e945d7e--5b64cc03-5dfe-4371-9937-b25cc7bf1871.lovable.app-1777359113262.png';

export const formatDate = (d: string): string =>
  new Date(d).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

export const getImageUrl = (item: Pick<NewsItem, 'images' | 'cover_image'>): string | null => {
  if (item.cover_image && typeof item.cover_image === 'string') {
    const raw = item.cover_image.trim();
    // Some legacy rows store cover_image as a JSON-encoded {"url":"..."} string.
    if (raw.startsWith('{')) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.url === 'string') return parsed.url;
      } catch {
        // fall through to raw
      }
    }
    return raw;
  }
  const imgs = Array.isArray(item.images) ? (item.images as { url?: string }[]) : [];
  const first = imgs[0];
  if (first && typeof first === 'object' && 'url' in first && typeof first.url === 'string') {
    return first.url;
  }
  if (typeof first === 'string') return first as string;
  return null;
};

// Extracts a [CANVA_EMBED:URL] marker from content and validates the URL
// is on the canva.com domain (whitelist — prevents arbitrary iframe XSS).
export const extractCanvaEmbed = (
  content: string | null | undefined,
): { url: string | null; cleanContent: string } => {
  if (!content) return { url: null, cleanContent: '' };
  const match = content.match(/\[CANVA_EMBED:(\S+?)\]/);
  if (!match) return { url: null, cleanContent: content };
  const candidate = match[1].trim();
  let url: string | null = null;
  try {
    const u = new URL(candidate);
    if (u.protocol === 'https:' && u.hostname === 'www.canva.com') {
      url = u.toString();
    }
  } catch {
    url = null;
  }
  return { url, cleanContent: content.replace(match[0], '').trim() };
};

// Extracts a [VIDEO_EMBED:URL] marker. Whitelists only the project's
// public Cloud storage bucket to prevent arbitrary <video src> injection.
export const extractVideoEmbed = (
  content: string,
): { url: string | null; cleanContent: string } => {
  const match = content.match(/\[VIDEO_EMBED:(\S+?)\]/);
  if (!match) return { url: null, cleanContent: content };
  const candidate = match[1].trim();
  let url: string | null = null;
  try {
    const u = new URL(candidate);
    const allowedHost = 'gytjxntqhtrnsqajpqfr.supabase.co';
    const allowedPrefix = '/storage/v1/object/public/';
    if (
      u.protocol === 'https:' &&
      u.hostname === allowedHost &&
      u.pathname.startsWith(allowedPrefix)
    ) {
      url = u.toString();
    }
  } catch {
    url = null;
  }
  return { url, cleanContent: content.replace(match[0], '').trim() };
};

export const newsUrl = (slug: string): string => `${SITE_URL}/aktualnosci/${slug}`;
